import { useRef, useCallback, type ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Building2,
  Upload,
  ImageIcon,
  Sparkles,
  FileText,
  ClipboardList,
  Target,
} from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { LockedBanner } from "../common/LockedBanner";
import { ProgressCard } from "../common/ProgressCard";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { VoiceTextareaField } from "../common/VoiceTextareaField";

const IMAGE_MIMES = ['image/jpeg', 'image/png'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

interface StoryGeneralCardProps {
  onCollapse: () => void;
}

type StoryGeneralFieldKey =
  | "leadershipIntroduction"
  | "leadershipKeyWins"
  | "leadershipAcknowledgements"
  | "leadershipLookingAhead"
  | "basisReportScope"
  | "basisLimitations"
  | "basisCertifications"
  | "practicesSustainabilityManagement"
  | "practicesConcretePractices"
  | "practicesTopInitiatives"
  | "strategyHotspots"
  | "strategyGoals"
  | "strategyPriorities"
  | "strategyFlagshipInitiatives"
  | "strategyChallenges";

function DetailField({
  id,
  label,
  placeholder,
  value,
  type = "text",
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  type?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold leading-5 text-slate-900">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="form-input h-12"
      />
    </div>
  );
}

export function StoryGeneralCard({ onCollapse }: StoryGeneralCardProps) {
  const {
    assessmentId,
    storyGeneralData: data,
    updateStoryGeneralData: update,
    uploadFile,
    forceSave,
    saveStatus,
    isLocked,
    isReadOnly,
    storyGeneralCompletion,
  } = useWizard();

  const disabled = isLocked || isReadOnly;
  const { toast } = useToast();
  const coverImageRef = useRef<HTMLInputElement>(null);
  const leadershipPhotoRef = useRef<HTMLInputElement>(null);
  const { data: certificationStatus } = useQuery({
    queryKey: ["assessment-has-certification", assessmentId],
    queryFn: async () => {
      const response = await fetch(`/api/assessments/${assessmentId}/has-certification`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to load certification status");
      }
      return response.json() as Promise<{ hasCertification: boolean }>;
    },
    enabled: !!assessmentId,
  });

  const shouldShowBasisCertifications = certificationStatus?.hasCertification === true;

  const handleCoverImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!IMAGE_MIMES.includes(file.type)) {
      toast({
        title: "Unsupported file type",
        description: "Only JPEG or PNG files are accepted.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10 MB.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    const result = await uploadFile(file, "logo");
    if (result.ok) {
      update({ coverImageUrl: result.data });
      toast({ title: "Cover image uploaded", description: file.name });
    } else {
      toast({ title: "Upload failed", description: result.error, variant: "destructive" });
    }
    e.target.value = "";
  };

  const handleLeadershipPhotoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!IMAGE_MIMES.includes(file.type)) {
      toast({
        title: "Unsupported file type",
        description: "Only JPEG or PNG files are accepted.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_BYTES) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10 MB.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    const result = await uploadFile(file, "logo");
    if (result.ok) {
      update({ leadershipPhotoUrl: result.data });
      toast({ title: "Photo uploaded", description: file.name });
    } else {
      toast({ title: "Upload failed", description: result.error, variant: "destructive" });
    }
    e.target.value = "";
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"General"}
        description={"Build the narrative sections of your sustainability report — leadership message, preparation basis, and sustainability strategy"}
        onClose={onCollapse}
        onSave={() => {
          void forceSave();
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Sustainability Story"}
      />

      {disabled && <LockedBanner />}

      <SectionCard
        header={
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">
                    {"1. Cover Page & Personalization"}
                  </h3>
                </div>
                <p className="max-w-3xl text-sm leading-6 text-slate-500">
                  {"Upload a cover picture to personalize the report to your company context"}
                </p>
              </div>
            </div>
          </div>
        }
      >
        <input
          id="story-general-cover-image"
          type="file"
          ref={coverImageRef}
          onChange={handleCoverImageUpload}
          accept=".jpg,.jpeg,.png"
          className="hidden"
          disabled={disabled}
        />

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)]">
          <div className="rounded-[28px] border-2 border-dashed border-primary/15 bg-primary/5 p-6 sm:p-8">
            <div className="flex flex-col items-start gap-4 text-left">
              <div className="flex h-14 w-14 items-center justify-center rounded-[20px] bg-white text-primary shadow-sm">
                <Upload className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h4 className="text-lg font-semibold leading-7 text-slate-900">
                  {data.coverImageUrl ? "Image uploaded" : "Upload a cover picture to personalize the report to your company context"}
                </h4>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={() => coverImageRef.current?.click()}
                  disabled={disabled}
                  className="rounded-3xl px-5"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload image
                </Button>
              </div>
              <p className="text-xs text-slate-400">JPEG or PNG only — max 10 MB</p>
            </div>
          </div>

          <div className="form-surface-card flex min-h-[240px] items-center justify-center p-4">
            {data.coverImageUrl ? (
              <img
                src={data.coverImageUrl}
                alt={"Cover preview"}
                className="h-full max-h-[280px] w-full rounded-[20px] object-cover"
              />
            ) : (
              <div className="flex items-center justify-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  <ImageIcon className="h-6 w-6" />
                </div>
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard
        header={
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">
                    {"2. Leadership Statement"}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <input
          id="story-general-leadership-photo"
          type="file"
          ref={leadershipPhotoRef}
          onChange={handleLeadershipPhotoUpload}
          accept=".jpg,.jpeg,.png"
          className="hidden"
          disabled={disabled}
        />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(200px,0.5fr)]">
          <div className="rounded-[28px] border-2 border-dashed border-primary/15 bg-primary/5 p-6 sm:p-8">
            <div className="flex flex-col items-start gap-4 text-left">
              <div className="flex h-14 w-14 items-center justify-center rounded-[20px] bg-white text-primary shadow-sm">
                <Upload className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h4 className="text-lg font-semibold leading-7 text-slate-900">
                  {data.leadershipPhotoUrl ? "Photo uploaded" : "Upload signer photo"}
                </h4>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={() => leadershipPhotoRef.current?.click()}
                  disabled={disabled}
                  className="rounded-3xl px-5"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload photo
                </Button>
              </div>
              <p className="text-xs text-slate-400">JPEG or PNG only — max 10 MB</p>
            </div>
          </div>

          <div className="form-surface-card flex min-h-[140px] items-center justify-center p-4">
            {data.leadershipPhotoUrl ? (
              <img
                src={data.leadershipPhotoUrl}
                alt="Leadership signer"
                className="h-full max-h-[180px] w-full rounded-[20px] object-cover"
              />
            ) : (
              <div className="flex items-center justify-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  <ImageIcon className="h-6 w-6" />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <DetailField
            id="story-general-leadership-signed-by"
            label={"Signed by (Name)"}
            placeholder={"e.g. John Smith"}
            value={data.leadershipSignedBy}
            onChange={(value) => update({ leadershipSignedBy: value })}
            disabled={disabled}
          />
          <DetailField
            id="story-general-leadership-title"
            label={"Title"}
            placeholder={"e.g. Chief Executive Officer"}
            value={data.leadershipTitle}
            onChange={(value) => update({ leadershipTitle: value })}
            disabled={disabled}
          />
          <DetailField
            id="story-general-leadership-signature-date"
            type="date"
            label={"Date of Signature"}
            placeholder={"Date of Signature"}
            value={data.leadershipSignatureDate}
            onChange={(value) => update({ leadershipSignatureDate: value })}
            disabled={disabled}
          />
        </div>

        <div className="grid gap-4">
          <VoiceTextareaField
            id="story-general-leadership-introduction"
            label={"Introduction — Who you are, why sustainability matters, and how your product/service creates value for customers and society."}
            placeholder={"e.g. We are a mid-sized manufacturing company dedicated to producing sustainable packaging. Sustainability is central to our mission because our customers demand it and our planet requires it..."}
            value={data.leadershipIntroduction}
            rows={5}
            disabled={disabled}
            onChange={(value) => update({ leadershipIntroduction: value })}
          />
          <VoiceTextareaField
            id="story-general-leadership-key-wins"
            label={"Key Wins & Metrics — Specific, credible wins and the 2–3 most meaningful sustainability actions/changes this year, including at least one number (e.g., % renewable, tCO₂e, kWh, waste %, training hours)."}
            placeholder={"e.g. This year we reduced our carbon emissions by 18% (tCO₂e), switched to 60% renewable electricity, and cut waste to landfill by 25%. These changes saved €120k in energy costs..."}
            value={data.leadershipKeyWins}
            disabled={disabled}
            onChange={(value) => update({ leadershipKeyWins: value })}
          />
          <VoiceTextareaField
            id="story-general-leadership-acknowledgements"
            label={"Acknowledgements & Certifications — Thanking staff/partners who drove progress and any certification/rating/verification achieved or renewed this year."}
            placeholder={"e.g. I want to thank our sustainability team and all department heads who made this possible. We are proud to have renewed our ISO 14001 certification and achieved a B Corp score of 82..."}
            value={data.leadershipAcknowledgements}
            rows={4}
            disabled={disabled}
            onChange={(value) => update({ leadershipAcknowledgements: value })}
          />
          <VoiceTextareaField
            id="story-general-leadership-looking-ahead"
            label={"Looking Ahead — Top priority/commitment for next year and who you want to work with (suppliers/customers/community) to deliver it."}
            placeholder={"e.g. Our top priority for next year is achieving Scope 1 carbon neutrality. We will work closely with our key suppliers to reduce upstream emissions and partner with local communities on reforestation..."}
            value={data.leadershipLookingAhead}
            rows={4}
            disabled={disabled}
            onChange={(value) => update({ leadershipLookingAhead: value })}
          />
        </div>
      </SectionCard>

      <SectionCard
        header={
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                <FileText className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">
                    {"3. B1 Basis for Preparation"}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <div className="grid gap-4">
          <VoiceTextareaField
            id="story-general-basis-report-scope"
            label={"Describe what this report covers (period + entities/sites), the framework used (VSME), and how you approached data collection."}
            placeholder={"e.g. This report covers the calendar year 2025 for all operations of [Company Name]. It follows the VSME standard and data was collected through..."}
            value={data.basisReportScope}
            disabled={disabled}
            onChange={(value) => update({ basisReportScope: value })}
          />
          <VoiceTextareaField
            id="story-general-basis-limitations"
            label={"Were there any gaps or estimates in your data this year? Briefly explain and how you’ll improve next year"}
            placeholder={"e.g. Scope 3 emissions were estimated using spend-based methods. Next year we plan to collect primary data from key suppliers..."}
            value={data.basisLimitations}
            disabled={disabled}
            onChange={(value) => update({ basisLimitations: value })}
          />
          {shouldShowBasisCertifications ? (
            <VoiceTextareaField
              id="story-general-basis-certifications"
              label={"If listing certifications: Why is this certification relevant to your stakeholders? What changed or improved because of it?"}
              placeholder={"e.g. Our ISO 14001 certification demonstrates our commitment to environmental management. Since obtaining it, we have reduced waste by 15%..."}
              value={data.basisCertifications}
              rows={4}
              hint={"Only relevant if your company holds certifications (e.g. ISO 14001, B Corp, etc.)"}
              disabled={disabled}
              onChange={(value) => update({ basisCertifications: value })}
            />
          ) : null}
        </div>
      </SectionCard>

      <SectionCard
        header={
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                <ClipboardList className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">
                    {"4. B2 & C2 Practices, Policies and Future Initiatives"}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <div className="grid gap-4">
          <VoiceTextareaField
            id="story-general-practices-management"
            label={"Briefly describe how you manage sustainability in practice (who owns it + how it's implemented)."}
            placeholder={"e.g. Our sustainability efforts are led by the COO with support from department heads. We hold quarterly sustainability reviews and..."}
            value={data.practicesSustainabilityManagement}
            disabled={disabled}
            onChange={(value) => update({ practicesSustainabilityManagement: value })}
          />
          <VoiceTextareaField
            id="story-general-practices-concrete-practices"
            label={"Mention 2 concrete practices from your table and how progress is tracked (1 KPI or review cadence)."}
            placeholder={"e.g. 1) We track energy consumption monthly and have reduced usage by 10% YoY. 2) Employee training on sustainability is reviewed quarterly..."}
            value={data.practicesConcretePractices}
            disabled={disabled}
            onChange={(value) => update({ practicesConcretePractices: value })}
          />
          <VoiceTextareaField
            id="story-general-practices-top-initiatives"
            label={"Summarize your top 2-3 initiatives for next year, why they matter, and the expected outcomes. Include one measurable target if available."}
            placeholder={"e.g. 1) Transition to 100% renewable energy by Q4 2026 — reduces Scope 2 emissions by ~40%. 2) Launch supplier sustainability assessment..."}
            value={data.practicesTopInitiatives}
            disabled={disabled}
            onChange={(value) => update({ practicesTopInitiatives: value })}
          />
        </div>
      </SectionCard>

      <SectionCard
        header={
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[20px] bg-primary/10 text-primary">
                <Target className="h-5 w-5" />
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-semibold leading-7 text-slate-900">
                    {"5. C1 Strategy: Business Model & Sustainability Initiatives"}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <div className="grid gap-4">
          <VoiceTextareaField
            id="story-general-strategy-hotspots"
            label={"Pick your top 3 sustainability hotspots. For each, explain in one short sentence why it matters to your business (cost/risk/compliance/market)."}
            placeholder={"e.g. 1) Energy costs — our biggest operational expense, directly tied to margins. 2) Supply chain risk — regulatory compliance in key markets. 3) Talent retention — employees increasingly value ESG commitment."}
            value={data.strategyHotspots}
            disabled={disabled}
            onChange={(value) => update({ strategyHotspots: value })}
          />
          <VoiceTextareaField
            id="story-general-strategy-goals"
            label={"What are you trying to achieve on sustainability over the next 2-3 years, and why?"}
            placeholder={"e.g. Over the next 3 years, we aim to achieve carbon neutrality for Scope 1 & 2 emissions and improve our ESG rating to attract institutional investors..."}
            value={data.strategyGoals}
            disabled={disabled}
            onChange={(value) => update({ strategyGoals: value })}
          />
          <VoiceTextareaField
            id="story-general-strategy-priorities"
            label={"Choose top 3 priorities for the next 12-24 months. For each, write one sentence on what will change."}
            placeholder={"e.g. 1) Install solar panels — will cover 30% of electricity needs. 2) Implement waste sorting — targeting 80% recycling rate. 3) Sustainability training — all employees certified by Q2."}
            value={data.strategyPriorities}
            disabled={disabled}
            onChange={(value) => update({ strategyPriorities: value })}
          />
          <VoiceTextareaField
            id="story-general-strategy-flagship-initiatives"
            label={"Select up to 3 flagship initiatives from your initiatives list to showcase in the strategy section."}
            placeholder={"e.g. 1) Green Fleet Transition Program 2) Zero Waste Manufacturing Initiative 3) Community Solar Partnership"}
            value={data.strategyFlagshipInitiatives}
            rows={4}
            disabled={disabled}
            onChange={(value) => update({ strategyFlagshipInitiatives: value })}
          />
          <VoiceTextareaField
            id="story-general-strategy-challenges"
            label={"What is your biggest sustainability challenge right now? In one sentence, describe the challenge and how you plan to manage it"}
            placeholder={"e.g. Our biggest challenge is balancing rapid growth with emission reduction — we'll manage it through carbon offsets while transitioning to renewables."}
            value={data.strategyChallenges}
            rows={4}
            disabled={disabled}
            onChange={(value) => update({ strategyChallenges: value })}
          />
        </div>
      </SectionCard>

      <ProgressCard completion={storyGeneralCompletion} />
    </div>
  );
}
